import React, { useMemo } from 'react'
import {
  ContextualMenu,
  type ContextualMenuItem,
} from '@enamelai/design-system'
import { IPhases, ITreatmentPlan } from '@dataTypes/treatmentPlan.types'
import './PhaseActionMenuButton.css'

export type PhaseMenuItem = {
  id: string
  label: string
  disabled?: boolean
}

interface PhaseActionMenuButtonProps {
  tabKey: string
  originalIdx: number
  phase: IPhases
  plan: ITreatmentPlan | null
  isEditMode: boolean
  canMoveUp?: boolean
  canMoveDown?: boolean
  onMenuSelect: (
    itemId: string,
    tabKey: string,
    originalIdx: number,
    phase: IPhases,
    plan: ITreatmentPlan | null,
    isEditMode: boolean,
  ) => void
  customMenuItems?: PhaseMenuItem[]
}

const stopHeaderToggle = (event: React.SyntheticEvent): void => {
  event.stopPropagation()
}

const PhaseActionMenuButton: React.FC<PhaseActionMenuButtonProps> = ({
  tabKey,
  originalIdx,
  phase,
  plan,
  isEditMode,
  canMoveUp = false,
  canMoveDown = false,
  onMenuSelect,
  customMenuItems,
}): React.ReactElement => {
  const items = useMemo((): ContextualMenuItem[] => {
    const source: PhaseMenuItem[] = customMenuItems ?? [
      {
        id: 'move-phase-up',
        label: 'Move up',
        disabled: !canMoveUp,
      },
      {
        id: 'move-phase-down',
        label: 'Move down',
        disabled: !canMoveDown,
      },
      {
        id: 'duplicate',
        label: 'Duplicate',
      },
      {
        id: 'delete',
        label: 'Delete',
      },
    ]
    return source.map((item) => ({
      id: item.id,
      label: item.label,
      disabled: item.disabled,
      onSelect: (): void => {
        onMenuSelect(item.id, tabKey, originalIdx, phase, plan, isEditMode)
      },
    }))
  }, [
    canMoveDown,
    canMoveUp,
    customMenuItems,
    isEditMode,
    onMenuSelect,
    originalIdx,
    phase,
    plan,
    tabKey,
  ])

  return (
    <div
      className="phase-action-menu-container"
      onClick={stopHeaderToggle}
      onMouseDown={stopHeaderToggle}
    >
      <ContextualMenu ariaLabel="Phase actions" items={items} />
    </div>
  )
}

export default PhaseActionMenuButton
